import { DashboardUnits } from '@dataTypes/dashboard.types'

export const formatNumber = (
  value: number | string,
  showCurrencySymbol: boolean,
): string => {
  let formattedValue: number
  if (typeof value === 'number') {
    formattedValue = value
  } else if (typeof value === 'string') {
    const parsed = parseFloat(value)
    formattedValue = isNaN(parsed) ? 0 : parsed
  } else {
    formattedValue = 0
  }
  const locale = 'en-US'
  const currency = 'USD'
  const options: Intl.NumberFormatOptions = {
    style: showCurrencySymbol ? 'currency' : 'decimal',
    currency: currency,
  }

  return new Intl.NumberFormat(locale, options).format(formattedValue)
}

export const formatValueByUnit = (
  value: string | number,
  units: DashboardUnits,
): string => {
  switch (units) {
    case '%':
      return `${formatNumber(value, false)}%`
    case '$':
      return `${formatNumber(value, true)}`
    case '#':
      return formatNumber(value, false)
    default:
      return `${value}`
  }
}

export const computeIncome = (
  current: number | string,
  previous: number | string,
): number => {
  const formattedCurrent =
    typeof current !== 'string' ? current : parseInt(current, 10)
  const formattedPrevious =
    typeof previous !== 'string' ? previous : parseInt(previous, 10)
  return ((formattedCurrent - formattedPrevious) / formattedPrevious) * 100
}

export const formatPhoneNumber = (
  phone: string,
  includeCountryCode: boolean = true,
): string => {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11)
    if (includeCountryCode)
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
    else
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`

  let formatted = ''

  if (includeCountryCode) formatted = '+1'

  if (cleaned.length === 10)
    return `${formatted}(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  else return phone
}

export const unformatPhoneNumber = (
  formattedPhone: string,
  removeCountryCode: boolean = false,
): string => {
  // Extract only digits from the formatted phone number
  const digitsOnly = formattedPhone.replace(/\D/g, '')

  // If removeCountryCode is true and we have 11 digits starting with '1', remove the leading '1'
  if (
    removeCountryCode &&
    digitsOnly.length === 11 &&
    digitsOnly.startsWith('1')
  ) {
    return digitsOnly.slice(1)
  }

  return digitsOnly
}

export const formatSSN = (ssn: string | undefined | null): string => {
  if (!ssn || typeof ssn !== 'string') return ''

  const cleaned = ssn.replace(/\D/g, '')
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`
  }
  // If already formatted or invalid length, return as-is
  return ssn
}

export const formatTaxId = (taxId: string | undefined | null): string => {
  if (!taxId || typeof taxId !== 'string') return ''

  const cleaned = taxId.replace(/\D/g, '')
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
  }
  // If already formatted or invalid length, return as-is
  return taxId
}

export const normalizePhoneNumber = (phoneNumber: string): string => {
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/[^0-9]/g, '')

  // If already 11 digits and starts with 1, return with + prefix
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`
  }

  // If 10 digits (US number without country code), prepend 1
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`
  }

  // If longer than 11 digits, take the last 11 digits
  if (digitsOnly.length > 11) {
    return `+${digitsOnly.slice(-11)}`
  }

  // If exactly 11 digits but doesn't start with 1, prepend 1 and take last 11
  if (digitsOnly.length === 11) {
    return `+1${digitsOnly.slice(-10)}`
  }

  // For numbers shorter than 10 digits, return with + prefix (cannot normalize properly)
  return `${digitsOnly}`
}

export const formatAmount = (
  value: number | string | null | undefined,
  valueIfEmpty?: string,
): string => {
  if (valueIfEmpty && (value == null || value === '' || value === undefined)) {
    return valueIfEmpty
  }

  const amount = Number(value)

  return `$${(Number.isNaN(amount) ? 0 : amount).toFixed(2)}`
}
